/**
 * System › Message Library — the wire contracts.
 *
 * Mirrors `AlpacaIntelligence.Application.Messages` over the XAF-shared
 * `dbo.MessageEditor`. See ADR-0017.
 */

/** The surface a message is shown on — the two-tier message standard (§18.6).
 *  Error / Warning / Confirm block in the centred dialog; Info / Success are
 *  transient in the top banner. */
export type Severity = 'Error' | 'Warning' | 'Confirm' | 'Info' | 'Success';

/** Where a row came from. `Legacy` is what an absent Source means — the rows
 *  ALPACA's own seeder created. */
export type MessageSourceKind = 'Legacy' | 'Catalog' | 'Admin';

/** Row lifecycle. `Customized` is DERIVED (a Catalog row whose wording an admin
 *  changed), never stored — see `statusOf`. */
export type MessageStatusKind = 'Active' | 'Customized' | 'Retired';

/** One row of the library grid, exactly as `GET /api/v1/messages` returns it. */
export interface MessageRow {
  oid: string;
  /** ALPACA's zero-padded running number, `001`…`390`. A string, not a number —
   *  sort it numerically. */
  msid: string | null;
  messageKey: string | null;
  message: string | null;
  /** Resolved `DefaultSetting.ModuleName`, never the Oid. Null on every legacy
   *  row: ALPACA's seeder never set it and its detail view could not. */
  module: string | null;
  moduleId: string | null;
  bo: string | null;
  boId: string | null;
  actionType: string | null;
  remark: string | null;
  /** Null means "not yet curated" — the screen offers `severitySuggestion`
   *  instead of inventing one. */
  severity: Severity | null;
  /** What the API makes of the wording. Computed server-side on every read and
   *  never stored: choosing a message's surface is logic, and the UI holds none
   *  (§1). Shown as a suggestion until an admin confirms it into `severity`. */
  severitySuggestion: Severity | null;
  /** Null reads as `Legacy`. */
  source: MessageSourceKind | null;
  /** The code-side default, kept beside an admin's edit. */
  catalogText: string | null;
  /** Server-derived: a catalog row whose wording an admin changed. */
  isCustomized: boolean;
  /** Null reads as `Active`. */
  status: string | null;
  navId: string | null;
  params: string | null;
  /** The legacy ALPACA forms that raise this message, busiest first — from the
   *  embedded code scan, NOT the database. Empty means legacy code never raises
   *  this key. */
  usedInForms: string[];
  /** How many SCREENS raise it — what the Used In column shows. */
  usedInScreens: number;
  /** Total call sites across those screens. */
  usedInSites: number;
  /** The module the code raises it in, or `Shared` when several do. DERIVED for
   *  display only; the record's own `module` is whatever an admin assigned. */
  derivedModule: string | null;
  /** The single screen that raises it, or null when several do. Derived. */
  derivedBo: string | null;
  translationCount: number;
  lastUpdatedDate: string | null;
  lastUpdatedBy: string | null;
}

/** One translation — the legacy `dbo.Translations` child, shared with Glossary. */
export interface MessageTranslation {
  oid: string;
  language: string | null;
  translation: string | null;
  lastUpdatedDate: string | null;
}

/** One audit row for this message, as GET /api/v1/audit/trail returns it.
 *  A SUBSET of that endpoint's shape — only the columns this panel shows. */
export interface MessageHistoryRow {
  oid: string;
  createdDate?: string | null;
  createdBy?: string | null;
  operationType?: string | null;
  propertyName?: string | null;
  oldValue?: string | null;
  newValue?: string | null;
  comment?: string | null;
}

/** One screen that raises a message — a row of the detail's Used In grid. */
export interface MessageUsageRow {
  /** The module that owns the screen. Blank when the scan could not resolve one. */
  module: string;
  /** The screen (business object) itself. */
  bo: string;
  /** How many call sites that screen has. */
  calls: number;
}

/** One record's full detail. */
export interface MessageDetail extends Omit<MessageRow, 'translationCount'> {
  /** The legacy source files behind `usedInForms`, so the attribution shown on
   *  the detail can be checked rather than taken on trust. */
  usedInFiles: string[];
  /** One row per screen that raises it — module + screen + call count. */
  usedIn: MessageUsageRow[];
  translations: MessageTranslation[];
}

/** A Module or BO option. `itemPath` is what makes the BO list cascade. */
export interface ModuleOption {
  id: string;
  name: string;
  itemPath: string | null;
}

/** The dropdown vocabularies, served from the same constants the API validates
 *  against so the two can never drift. */
export interface Vocabularies {
  severities: Severity[];
  actionTypes: string[];
  languages: string[];
}

/** Editable payload for create / update. */
export interface MessageInput {
  messageKey?: string | null;
  message?: string | null;
  severity?: string | null;
  moduleId?: string | null;
  boId?: string | null;
  actionType?: string | null;
  remark?: string | null;
  navId?: string | null;
  params?: string | null;
}

/** One quality observation about a row, rendered as a pill in the Checks column.
 *  `tone` maps to the shared pill classes. */
export interface MessageCheck {
  tone: 'alert' | 'warn' | 'info' | 'mute';
  label: string;
}


/** The columns of the Import / Template CSV. Wording only: MSID is shown for
 *  the operator's reference and MessageKey is what rows are MATCHED on, so
 *  neither is ever written back by an import. */
export const IMPORT_COLUMNS = ['MSID', 'MessageKey', 'Message', 'Remark'] as const;
